import { useContext, useState } from 'react';

import { getParticipantFormRequest, submitFormResponseRequest } from '@vyce/core/src/api/forms';
import { ChatUser, FormAnswer, FormDoc, Tag, User } from '@vyce/core/src/types';

import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useHistory } from 'react-router-dom';

const niNumberFromUser = (user: User): string | undefined => {
  const raw = user.context?.ni_number || user.employee?.ni_number;
  if (raw == null || String(raw).trim() === '') {
    return undefined;
  }
  return String(raw).trim();
};

const participantFromUser = (user: User): ChatUser => {
  const participant: ChatUser = {
    uid: user.uuid,
    name: user.first_name + ' ' + user.last_name,
    email: user.email,
    phone: user.phone,
  };
  const ni = niNumberFromUser(user);
  if (ni) {
    participant.niNumber = ni;
  }
  const emp = user.employee;
  if (emp?.job_title) {
    const jt = emp.job_title;
    participant.jobTitle = typeof jt === 'string' ? jt : (jt as Tag).name;
  }
  return participant;
};

export const useParticipantForm = ({ user, companyId }: { user: User; companyId: string }) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const history = useHistory();
  const [form, setForm] = useState<FormDoc | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const getForm = async (id: string) => {
    if (!companyId || !user?.uuid) {
      return;
    }
    try {
      setLoading(true);
      const formRes = await getParticipantFormRequest({
        companyId,
        formId: id,
        participant: participantFromUser(user),
      });
      setForm(formRes);
    } catch (error) {
      handleServerError(error);
    } finally {
      setLoading(false);
    }
  };

  const submitFormResponse = async (answers: FormAnswer[]) => {
    if (!companyId || !user?.uuid) {
      return;
    }
    try {
      setLoading(true);
      await submitFormResponseRequest({
        companyId,
        formId: form?.id ?? '',
        participant: participantFromUser(user),
        answers,
      });
      showNotification({ message: 'Form response has been submitted', options: { variant: 'success' } });
      history.push('/forms/my-forms');
    } catch (error) {
      handleServerError(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    form,
    loading,
    getForm,
    submitFormResponse,
  };
};
